/**
 * ImagePreview - Component for displaying generated/edited images.
 *
 * Features:
 * - Full-size image preview
 * - Image selector for multiple results
 * - Save to Media Library button
 * - Refine button for iterative improvements
 * - Loading state during generation
 */
import { useCallback, useRef, useState } from 'react';
import { useCreativeStudioStore, useSelectedImage, useRefinementInfo } from '@/store/creativeStudioStore';
import { useAddSessionReferenceMark } from '@/hooks/queries/useCreativeStudioQueries';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { AISparkleIcon } from '@/components/ui/Icons';
import {
  BookmarkIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { MarkerOverlay } from './MarkerOverlay';
import type { MarkBoundingBox } from './MarkerOverlay';
import type { MarkShade } from '@/types/creativeStudio';
import {
  MAX_REFINEMENT_REFERENCES,
  MAX_REFINEMENT_MARKS,
} from '@/config/creativeStudio';
import toast from 'react-hot-toast';

interface ImagePreviewProps {
  onStartRefine?: () => void;
  onSave?: () => void;
}

export function ImagePreview({ onStartRefine, onSave }: ImagePreviewProps) {
  const {
    generatedImages,
    selectedImageIndex,
    selectImage,
    isGenerating,
    isRefining,
    currentJobResult,
    setShowSaveModal,
    refinementSessionId,
    updateRefinementSession,
    activeTab,
  } = useCreativeStudioStore();

  const selectedImage = useSelectedImage();
  const refinementInfo = useRefinementInfo();
  const addMarkMutation = useAddSessionReferenceMark();

  // Hand the rendered <img> element to the MarkerOverlay so it can position
  // a canvas precisely over it. Use state instead of a plain ref because the
  // overlay needs to re-render when the image element changes (e.g., when
  // switching between generated images).
  const [imageEl, setImageEl] = useState<HTMLImageElement | null>(null);

  // Container ref isn't strictly needed but documents intent: the marker
  // overlay positions itself absolutely *within* this container.
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleMarkCreated = useCallback(
    async (blob: Blob, shade: MarkShade, bbox: MarkBoundingBox | null) => {
      if (!refinementSessionId) return;
      try {
        const session = await addMarkMutation.mutateAsync({
          sessionId: refinementSessionId,
          blob,
          markShade: shade,
          bbox,
        });
        updateRefinementSession(session);
        // Walk from the tail to find the just-added mark — `findLast` would
        // be cleaner but we'd rather not depend on ES2023 here.
        let newMarkName: string | null = null;
        for (let i = session.reference_images.length - 1; i >= 0; i--) {
          const r = session.reference_images[i];
          if (r.is_session_mark) {
            newMarkName = r.name;
            break;
          }
        }
        toast.success(
          newMarkName ? `Added ${newMarkName}` : 'Reference mark added'
        );
      } catch (error) {
        console.error('Failed to add reference mark:', error);
        toast.error('Failed to add reference mark');
      }
    },
    [refinementSessionId, addMarkMutation, updateRefinementSession]
  );

  // Handle save to media library
  const handleSave = () => {
    if (onSave) {
      onSave();
    } else {
      setShowSaveModal(true);
    }
  };

  // Handle start refinement
  const handleStartRefine = () => {
    if (onStartRefine) {
      onStartRefine();
    }
  };

  // Marker-cap surfacing. Caps live in @/config/creativeStudio (mirrored
  // from the backend). Computed here so the user-visible cap copy stays
  // in one place even if MarkerOverlay grows additional disabled paths.
  const sessionRefs = refinementInfo.session?.reference_images ?? [];
  const markCount = sessionRefs.filter((r) => r.is_session_mark).length;
  const totalRefCount = sessionRefs.length;
  let markerUnavailableReason: string | null = null;
  if (markCount >= MAX_REFINEMENT_MARKS) {
    markerUnavailableReason = `Maximum ${MAX_REFINEMENT_MARKS} markers per session`;
  } else if (totalRefCount >= MAX_REFINEMENT_REFERENCES) {
    markerUnavailableReason = `Maximum ${MAX_REFINEMENT_REFERENCES} references per session`;
  }

  // Refinement keeps the current image visible and overlays a small
  // status indicator (see the block at the bottom of the render).
  // Generation has no prior image to show on the first call, so it keeps
  // the loading-card swap below.

  // Show loading state
  if (isGenerating) {
    return (
      <Card className="p-6 flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <PhotoIcon className="h-8 w-8 text-primary-500 animate-bounce" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">Generating your image...</h3>
          <p className="text-sm text-gray-500">This may take 10-30 seconds</p>
          <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 rounded-full animate-progress" />
          </div>
        </div>
      </Card>
    );
  }

  // Show error state
  if (currentJobResult?.status === 'failed') {
    return (
      <Card className="p-6 flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PhotoIcon className="h-8 w-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Generation Failed</h3>
          <p className="text-sm text-red-600 max-w-sm">
            {currentJobResult.error_message || 'An error occurred during generation.'}
          </p>
        </div>
      </Card>
    );
  }

  // Show empty state
  if (!selectedImage) {
    return (
      <Card className="p-6 flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PhotoIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Image Yet</h3>
          <p className="text-sm text-gray-500 max-w-sm">
            Enter a prompt and click Generate to create your first image.
          </p>
        </div>
      </Card>
    );
  }

  // Check if signed URL is available
  const hasValidUrl = selectedImage.signed_url && selectedImage.signed_url.length > 0;

  return (
    <Card className="p-6">
      {/* Image Display */}
      <div
        ref={containerRef}
        className="relative bg-gray-100 rounded-lg overflow-hidden mb-4 flex justify-center"
      >
        {hasValidUrl ? (
          // Intentionally NO crossOrigin attr: with crossOrigin="anonymous"
          // set, the browser refuses to display the image unless GCS returns
          // matching CORS headers, which it won't in unconfigured buckets.
          // The marker flow never draws this cross-origin <img> into a
          // canvas — it exports only the transparent stroke overlay and the
          // backend composites onto the session image — so display works
          // for everyone AND marker creation works without bucket CORS.
          <img
            ref={setImageEl}
            src={selectedImage.signed_url}
            alt="Generated image"
            className="w-full h-auto object-contain max-h-[70vh]"
          />
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[300px] text-gray-400">
            <PhotoIcon className="h-16 w-16 mb-2" />
            <p className="text-sm">Image URL not available</p>
          </div>
        )}
        {/* Marker overlay belongs to the Refine flow only. `isActive`
            stays true while a session lives, so we additionally gate on
            `activeTab === 'edit'` to keep the markers hidden when the
            user flips back to the Generate tab mid-session. Also gate on
            `canRefine`: if the user has used all refinements there's
            nothing to apply a new mark to, and the "Maximum refinements
            reached" banner in RefinementPanel already directs them to
            save or revert — letting them draw more marks would silently
            waste uploads. */}
        {hasValidUrl &&
          activeTab === 'edit' &&
          refinementInfo.isActive &&
          refinementInfo.canRefine && (
          <MarkerOverlay
            imageEl={imageEl}
            brightnessGrid={refinementInfo.session?.brightness_grid ?? null}
            disabled={isRefining}
            isUploading={addMarkMutation.isPending}
            unavailableReason={markerUnavailableReason}
            onMarkCreated={handleMarkCreated}
          />
        )}
        {/* Refining-in-flight overlay. Keeps the current image visible
            (so the page doesn't collapse to a 400px loading card and shift
            everything below) while making it obvious work is happening.
            This overlay sits at z-30 above the MarkerOverlay canvas
            (z-10) and so blocks pointer events on it; that overlap is
            redundant defense — MarkerOverlay already gates its canvas
            with `disabled={isRefining}` (line 201). If you ever make
            the refining-overlay opt-out or change its z-index, recheck
            that the canvas can't accept strokes mid-refinement. */}
        {hasValidUrl && isRefining && (
          <div
            className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur-sm text-white"
            role="status"
            aria-live="polite"
          >
            <div className="animate-spin h-8 w-8 border-2 border-white border-t-transparent rounded-full" />
            <p className="text-sm font-medium">Refining your image…</p>
            <p className="text-xs opacity-80">This may take 10–30 seconds</p>
          </div>
        )}
        {/* Image Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
          <div className="flex items-center justify-between text-white text-sm">
            <span>
              {selectedImage.width} x {selectedImage.height}px
            </span>
            <span>{selectedImage.aspect_ratio}</span>
          </div>
        </div>
      </div>

      {/* Image Selector (if multiple images) */}
      {generatedImages.length > 1 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {generatedImages.map((img, index) => (
            <button
              key={img.index}
              onClick={() => selectImage(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors
                ${
                  selectedImageIndex === index
                    ? 'border-primary-500'
                    : 'border-transparent hover:border-gray-300'
                }`}
            >
              {img.signed_url ? (
                <img
                  src={img.signed_url}
                  alt={`Generated image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <PhotoIcon className="h-6 w-6 text-gray-400" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Action Buttons — Save and Refine share the row equally so neither
          stretches longer than the other. When Refine is hidden (no
          completed generation, or refinement is active), Save still
          fills the row via flex-1. */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          mobileTouch
          className="flex-1"
          disabled={!selectedImage}
        >
          <BookmarkIcon className="h-4 w-4 mr-2" />
          Save to Media Library
        </Button>
        {/* Show Refine button if we have a completed generation job */}
        {currentJobResult?.status === 'completed' && !refinementInfo.isActive && (
          <Button
            variant="secondary"
            size="md"
            onClick={handleStartRefine}
            disabled={!hasValidUrl}
            mobileTouch
            className="flex-1"
          >
            Refine
          </Button>
        )}
      </div>

      {/* Refinement Status */}
      {refinementInfo.isActive && (
        <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AISparkleIcon className="h-4 w-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">Refinement Active</span>
            </div>
            <span className="text-xs text-primary-600">
              {refinementInfo.refinementCount}/{refinementInfo.maxRefinements} refinements
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}
